import React, { useState } from "react";
import { useAuth } from "@/admin/AuthContext";
import { KeyRound, Eye, EyeOff, Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const PasswordField = ({ label, value, onChange, testid, autoComplete }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="mb-4">
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          data-testid={testid}
          className="w-full px-4 py-3 pr-12 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          data-testid={`${testid}-toggle`}
          aria-label={show ? "Sembunyikan password" : "Tampilkan password"}
          title={show ? "Sembunyikan password" : "Tampilkan password"}
          className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-400 hover:text-slate-700 transition-colors"
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};

export default function ChangePasswordPanel() {
  const { authAxios, user, setSession } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setDone(false);
    if (newPassword.length < 8) {
      toast.error("Password baru minimal 8 karakter");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Konfirmasi password baru tidak cocok");
      return;
    }
    if (newPassword === currentPassword) {
      toast.error("Password baru harus berbeda dari password lama");
      return;
    }
    setSaving(true);
    try {
      const { data } = await authAxios.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      // Adopt the fresh token so this session stays valid (other sessions are
      // force-logged-out by the backend password_changed_at check).
      if (data?.access_token) setSession(data.access_token);
      toast.success("Password berhasil diubah. Sesi lain telah dikeluarkan.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Gagal mengubah password";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-lg" data-testid="change-password-panel">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-blue-800 text-white flex items-center justify-center shrink-0">
          <KeyRound className="w-5 h-5" />
        </div>
        <div>
          <h1 className="font-heading font-bold text-xl text-slate-900">Ganti Password</h1>
          <p className="text-sm text-slate-500">
            Perbarui password akun {user?.email ? <b>{user.email}</b> : "Anda"}.
          </p>
        </div>
      </div>

      <form
        onSubmit={submit}
        className="mt-4 bg-white rounded-2xl border border-slate-200 p-5"
        data-testid="change-password-form"
      >
        <PasswordField
          label="Password saat ini"
          value={currentPassword}
          onChange={setCurrentPassword}
          testid="change-password-current"
          autoComplete="current-password"
        />
        <PasswordField
          label="Password baru (min. 8 karakter)"
          value={newPassword}
          onChange={setNewPassword}
          testid="change-password-new"
          autoComplete="new-password"
        />
        <PasswordField
          label="Konfirmasi password baru"
          value={confirmPassword}
          onChange={setConfirmPassword}
          testid="change-password-confirm"
          autoComplete="new-password"
        />

        <button
          type="submit"
          disabled={saving || !currentPassword || !newPassword || !confirmPassword}
          data-testid="change-password-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-blue-800 hover:bg-blue-900 disabled:opacity-60 text-white font-medium px-6 py-3 rounded-xl transition mt-1"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
          {saving ? "Menyimpan..." : "Ubah Password"}
        </button>

        {done && (
          <div
            className="mt-4 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
            data-testid="change-password-success"
          >
            <CheckCircle2 className="w-4 h-4" />
            Password berhasil diubah. Gunakan password baru saat login berikutnya.
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-400">
          <ShieldCheck className="h-3.5 w-3.5" />
          Kami memverifikasi password lama Anda sebelum menyimpan yang baru.
        </div>
      </form>
    </div>
  );
}
